import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { DemoFan } from '../../datas/demoFan';
import { WorkflowCardData } from '../../pages/SessionPage/Generation/WorkflowVisualization/types';
import { GenerationResponse } from '../../services/generationsApi';

interface UseWorkflowCardsParams {
  isDesignerMode: boolean;
  isLoadingDesignImages: boolean;
  isRegeneratingMusic: boolean;
  isRegeneratingImage: boolean;
  designImage1: string | null;
  designImage2: string | null;
  song1Ready: boolean;
  song2Ready: boolean;
  selectedImageId: 1 | 2 | null;
  selectedSongId: 1 | 2 | null;
  isMusicPlaying?: boolean;
  isGenerationPlaylistActive?: boolean;
  showImage1: string | null;
  showImage2: string | null;
  thumb1Ready: boolean;
  thumb2Ready: boolean;
  activeMusicGeneration?: GenerationResponse;
  /** When set, both song cards show this (shared lyrics title); otherwise API title1/title2. */
  sharedTrackTitle?: string | null;
  fanData?: DemoFan;
  handleCardClick: (songVersion: 1 | 2) => void;
  handleCardSelect: (songVersion: 1 | 2) => void;
  handleCardRegenerate: (
    cardId: 'card1' | 'card2',
    prompt: string,
  ) => Promise<void>;
  handleImageSelect: (imageVersion: 1 | 2) => void;
  onPause?: () => void;
}

export const useWorkflowCards = ({
  isDesignerMode,
  isLoadingDesignImages,
  isRegeneratingMusic,
  isRegeneratingImage,
  designImage1,
  designImage2,
  song1Ready,
  song2Ready,
  selectedImageId,
  selectedSongId,
  isMusicPlaying = false,
  isGenerationPlaylistActive = false,
  showImage1,
  showImage2,
  thumb1Ready,
  thumb2Ready,
  activeMusicGeneration,
  sharedTrackTitle,
  fanData,
  handleCardClick,
  handleCardSelect,
  handleCardRegenerate,
  handleImageSelect,
  onPause,
}: UseWorkflowCardsParams): WorkflowCardData[] => {
  const { t } = useTranslation();
  return useMemo(() => {
    const cards: WorkflowCardData[] = [];

    const card1Visible =
      (isDesignerMode && isLoadingDesignImages) ||
      (!isDesignerMode && isRegeneratingMusic) ||
      thumb1Ready;
    const card2Visible =
      (isDesignerMode && isLoadingDesignImages) ||
      (!isDesignerMode && isRegeneratingMusic) ||
      thumb2Ready;

    const card1Ready = isDesignerMode ? !!designImage1 : song1Ready;
    const card2Ready = isDesignerMode ? !!designImage2 : song2Ready;
    const card1Regenerating = isDesignerMode
      ? isRegeneratingImage && selectedImageId === 1
      : (isRegeneratingMusic || isRegeneratingImage) && selectedSongId === 1;
    const card2Regenerating = isDesignerMode
      ? isRegeneratingImage && selectedImageId === 2
      : (isRegeneratingMusic || isRegeneratingImage) && selectedSongId === 2;

    const sharedMusicTitle = sharedTrackTitle?.trim();
    const musicCardTitle1 =
      sharedMusicTitle ||
      activeMusicGeneration?.title1 ||
      'Version 1';
    const musicCardTitle2 =
      sharedMusicTitle ||
      activeMusicGeneration?.title2 ||
      'Version 2';

    cards.push({
      id: 'card1',
      visible: card1Visible,
      imageUrl: showImage1 || undefined,
      isReady: card1Ready,
      isRegenerating: card1Regenerating,
      isSelected:
        card1Visible &&
        ((isDesignerMode && selectedImageId === 1) ||
          (!isDesignerMode && selectedSongId === 1)),
      showGradient:
        card1Visible &&
        (isDesignerMode
          ? (isLoadingDesignImages && !designImage1) || card1Regenerating
          : (!card1Ready && isRegeneratingMusic) || card1Regenerating),
      onClick: () => {
        if (isDesignerMode) {
          if (designImage1) {
            handleImageSelect(1);
          }
        } else if (showImage1) {
          handleCardSelect(1);
        }
      },
      onActionClick: () => {
        if (isDesignerMode) {
          if (designImage1) {
            window.open(designImage1, '_blank');
          }
        } else if (song1Ready) {
          if (selectedSongId === 1 && isMusicPlaying && onPause) {
            onPause();
          } else {
            handleCardClick(1);
          }
        }
      },
      onRegenerate: (prompt: string) => {
        void handleCardRegenerate('card1', prompt);
      },
      actionType: isDesignerMode ? 'external' : 'play',
      actionReady: isDesignerMode ? !!designImage1 : song1Ready,
      isPlaying:
        !isDesignerMode &&
        selectedSongId === 1 &&
        isMusicPlaying &&
        isGenerationPlaylistActive,
      title: isDesignerMode
        ? designImage1
          ? 'Design 1'
          : t('generation.generating')
        : song1Ready
          ? musicCardTitle1
          : t('generation.generating'),
      subtitle: isDesignerMode
        ? `@${fanData?.alias_tag || 'designer'}`
        : `@${fanData?.alias_tag || 'artist'}`,
      isDesignerMode,
    });

    cards.push({
      id: 'card2',
      visible: card2Visible,
      imageUrl: showImage2 || undefined,
      isReady: card2Ready,
      isRegenerating: card2Regenerating,
      isSelected:
        card2Visible &&
        ((isDesignerMode && selectedImageId === 2) ||
          (!isDesignerMode && selectedSongId === 2)),
      showGradient:
        card2Visible &&
        (isDesignerMode
          ? (isLoadingDesignImages && !designImage2) || card2Regenerating
          : (!card2Ready && isRegeneratingMusic) || card2Regenerating),
      onClick: () => {
        if (isDesignerMode) {
          if (designImage2) {
            handleImageSelect(2);
          }
        } else if (showImage2) {
          handleCardSelect(2);
        }
      },
      onActionClick: () => {
        if (isDesignerMode) {
          if (designImage2) {
            window.open(designImage2, '_blank');
          }
        } else if (song2Ready) {
          if (selectedSongId === 2 && isMusicPlaying && onPause) {
            onPause();
          } else {
            handleCardClick(2);
          }
        }
      },
      onRegenerate: (prompt: string) => {
        void handleCardRegenerate('card2', prompt);
      },
      actionType: isDesignerMode ? 'external' : 'play',
      actionReady: isDesignerMode ? !!designImage2 : song2Ready,
      isPlaying:
        !isDesignerMode &&
        selectedSongId === 2 &&
        isMusicPlaying &&
        isGenerationPlaylistActive,
      title: isDesignerMode
        ? designImage2
          ? 'Design 2'
          : t('generation.generating')
        : song2Ready
          ? musicCardTitle2
          : t('generation.generating'),
      subtitle: isDesignerMode
        ? `@${fanData?.alias_tag || 'designer'}`
        : `@${fanData?.alias_tag || 'artist'}`,
      isDesignerMode,
    });

    return cards;
  }, [
    isDesignerMode,
    isLoadingDesignImages,
    isRegeneratingMusic,
    isRegeneratingImage,
    designImage1,
    designImage2,
    song1Ready,
    song2Ready,
    selectedImageId,
    selectedSongId,
    isMusicPlaying,
    isGenerationPlaylistActive,
    showImage1,
    showImage2,
    thumb1Ready,
    thumb2Ready,
    activeMusicGeneration?.title1,
    activeMusicGeneration?.title2,
    sharedTrackTitle,
    fanData?.alias_tag,
    handleCardClick,
    handleCardSelect,
    handleCardRegenerate,
    handleImageSelect,
    onPause,
  ]);
};
